import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { defaultRouter, RouteContext } from 'app/src/router';
import isEqual from 'react-fast-compare';
import { navigate } from 'app/scripts/controller/navigate';
import { useSavedViewContextQuery } from './SavedViewContextQuery.generated';
import { useUpdateViewInOrganizationViewMutation } from './UpdateViewInOrganizationViewMutation.generated';
import {
  BoardTableViewFilter,
  UrlParams,
} from './filters/BoardTableViewFilter';
import { DueFilter } from './filters/DueFilter';
import { MembersFilter } from './filters/MembersFilter';
import { ListFilter } from './filters/ListFilter';
import { LabelsFilter } from './filters/LabelsFilter';
import { BoardsFilter } from './filters/BoardsFilter';
import { Spinner } from '@trello/nachos/spinner';
import { useMostRelevantBoards } from './useMostRelevantBoards';
import { maxSelectableBoards } from 'app/src/components/TeamBoardSelector/maxSelectableBoards';

export interface ViewFilters extends Record<string, BoardTableViewFilter> {
  boards: BoardsFilter;
  due: DueFilter;
  members: MembersFilter;
  list: ListFilter;
  labels: LabelsFilter;
}
const filterTypes: {
  name: keyof ViewFilters;
  Filter: new () => BoardTableViewFilter;
}[] = [
  { name: 'boards', Filter: BoardsFilter },
  { name: 'due', Filter: DueFilter },
  { name: 'members', Filter: MembersFilter },
  { name: 'list', Filter: ListFilter },
  { name: 'labels', Filter: LabelsFilter },
];

export interface SavedViewBase {
  filters: ViewFilters;
  editable: boolean;
}

export interface SavedViewEditable extends SavedViewBase {
  editable: true;
  setFilter: (filter: BoardTableViewFilter) => void;
  clearNonBoardFilters: () => void;
}

export interface SavedViewReadOnly extends SavedViewBase {
  editable: false;
}

export type SavedView = SavedViewEditable | SavedViewReadOnly;

export const getEmptyViewFilters = (): ViewFilters =>
  Object.fromEntries(
    filterTypes.map(({ name, Filter }) => [name, new Filter()]),
  ) as ViewFilters;

interface SavedViewContextValue<T extends SavedViewBase> {
  savedView: T;
}

export const SavedViewContext = React.createContext<
  SavedViewContextValue<SavedViewEditable | SavedViewReadOnly>
>({
  savedView: {
    filters: getEmptyViewFilters(),
    editable: false,
  },
});

interface WorkspaceViewProviderProps {
  idWorkspaceView: string;
  idView: string;
  children: React.ReactNode;
}

export const WorkspaceViewProvider: React.FunctionComponent<WorkspaceViewProviderProps> = ({
  idWorkspaceView,
  idView,
  children,
}) => {
  const { data, error, loading } = useSavedViewContextQuery({
    variables: { idOrganizationView: idWorkspaceView },
  });
  const [
    updateViewInOrganizationView,
  ] = useUpdateViewInOrganizationViewMutation();

  // Currently we only support 1 view per organization view, but the schema is set up to allow multiple views
  const view = useMemo(() => {
    return data?.organizationView?.views.find((view) => view.id === idView);
  }, [data?.organizationView?.views, idView]);

  // Currently we only support 1 criteria, but the schema is set up to allow multiple criteria
  const cardFilterCriteria = useMemo(() => {
    return (
      view?.cardFilter.criteria[0] || {
        // Default empty criteria if there isn't one defined yet
        __typename: 'OrganizationView_View_CardFilter_Criteria' as 'OrganizationView_View_CardFilter_Criteria',
      }
    );
  }, [view?.cardFilter.criteria]);

  const setNewCardFilterCriteria = useCallback(
    (newCardFilterCriteria) => {
      if (!data?.organizationView || !view || !cardFilterCriteria || loading) {
        return;
      }

      const newView = {
        ...view,
        cardFilter: {
          ...view.cardFilter,
          criteria: [
            newCardFilterCriteria, // Overwrite the first criteria in the array
            ...view!.cardFilter.criteria.slice(1),
          ],
        },
        id: idView,
      };

      updateViewInOrganizationView({
        variables: {
          idOrganizationView: idWorkspaceView,
          idView,
          view: newView,
        },
        // NW 4/16/21: Leaving optimistic response off for now because of issues with re-rendering and having to construct response typenames
        // optimisticResponse: {
        //   __typename: 'Mutation',
        //   updateViewInOrganizationView: {
        //     ...data.organizationView,
        //     views: data.organizationView.views.map((view) =>
        //       view.id === newView.id ? newView : view,
        //     ),
        //   },
        // },
      });
    },
    [
      cardFilterCriteria,
      data?.organizationView,
      idView,
      idWorkspaceView,
      loading,
      updateViewInOrganizationView,
      view,
    ],
  );

  const setFilter = useCallback(
    (filter: BoardTableViewFilter) => {
      const newCardFilterCriteria = {
        ...cardFilterCriteria,
        ...filter.serializeToView(),
      };

      if (isEqual(newCardFilterCriteria, cardFilterCriteria)) {
        return;
      }
      setNewCardFilterCriteria(newCardFilterCriteria);
    },
    [cardFilterCriteria, setNewCardFilterCriteria],
  );

  const clearNonBoardFilters = useCallback(() => {
    if (!data?.organizationView || !view || !cardFilterCriteria || loading) {
      return;
    }

    const newCardFilterCriteria = {
      ...cardFilterCriteria,
      ...new LabelsFilter().serializeToView(),
      ...new DueFilter().serializeToView(),
      ...new MembersFilter().serializeToView(),
      ...new ListFilter().serializeToView(),
    };

    setNewCardFilterCriteria(newCardFilterCriteria);
  }, [
    cardFilterCriteria,
    data?.organizationView,
    loading,
    setNewCardFilterCriteria,
    view,
  ]);

  const providerValue: SavedViewContextValue<SavedViewEditable> = useMemo(() => {
    const viewFilters = getEmptyViewFilters();

    for (const { name, Filter } of filterTypes) {
      const filter = new Filter();
      filter.deserializeFromView(cardFilterCriteria);
      viewFilters[name] = filter;
    }

    return {
      savedView: {
        filters: viewFilters,
        editable: true,
        setFilter,
        clearNonBoardFilters,
      },
    };
  }, [cardFilterCriteria, setFilter, clearNonBoardFilters]);

  if (error) {
    throw error;
  }
  if (loading) {
    return <Spinner />;
  }

  return (
    <SavedViewContext.Provider value={providerValue}>
      {children}
    </SavedViewContext.Provider>
  );
};

export const UrlParamsProvider: React.FunctionComponent = ({ children }) => {
  const urlParamsFromRouteContext = useCallback((): UrlParams => {
    const routeContext = defaultRouter.getRoute();
    const searchParams = routeContext.url?.searchParams;

    return searchParams ? Object.fromEntries(searchParams.entries()) : {};
  }, []);

  const [urlParams, setUrlParams] = useState(urlParamsFromRouteContext());

  const navigateWithUrlParams = useCallback((filter: BoardTableViewFilter) => {
    const routeContext = defaultRouter.getRoute();
    if (!routeContext?.url) {
      return;
    }
    const newUrlParams = filter.toUrlParams();
    const newUrl = new URL(routeContext.url.toString());

    for (const [key, value] of Object.entries(newUrlParams)) {
      if (!value) {
        newUrl.searchParams.delete(key);
      } else {
        newUrl.searchParams.set(key, value);
      }
    }

    if (routeContext.url.toString() === newUrl.toString()) {
      return;
    }

    // Hack: The URL api auto-encodes commas and colons, but we want to use "invalid" urls
    // with `,` and `:` characters in them.
    const search = newUrl.search.replace(/%2C/g, ',').replace(/%3A/g, ':');

    navigate(`${newUrl.pathname}${search}`, { replace: true });
    defaultRouter.updateSubscribers();
  }, []);

  useEffect(() => {
    return defaultRouter.subscribe((routeContext) => {
      setUrlParams((prevUrlParams) => {
        const newUrlParams = urlParamsFromRouteContext();

        if (isEqual(prevUrlParams, newUrlParams)) {
          return prevUrlParams; // Optimization: maintain reference equality to reduce re-renders
        }

        return newUrlParams;
      });
    });
  }, [urlParamsFromRouteContext]);

  const clearNonBoardFilters = useCallback(() => {
    const emptyFilters = getEmptyViewFilters();
    for (const [name, filter] of Object.entries(emptyFilters)) {
      if (name !== 'boards') {
        navigateWithUrlParams(filter);
      }
    }
  }, [navigateWithUrlParams]);

  const providerValue: SavedViewContextValue<SavedViewEditable> = useMemo(() => {
    const viewFilters: ViewFilters = getEmptyViewFilters();
    for (const { name, Filter } of filterTypes) {
      const filter = new Filter();
      filter.fromUrlParams(urlParams);
      viewFilters[name] = filter;
    }

    return {
      savedView: {
        filters: viewFilters,
        editable: true,
        setFilter: navigateWithUrlParams,
        clearNonBoardFilters,
      },
    };
  }, [urlParams, navigateWithUrlParams, clearNonBoardFilters]);

  return (
    <SavedViewContext.Provider value={providerValue}>
      {children}
    </SavedViewContext.Provider>
  );
};

interface DefaultViewProviderProps {
  defaultViewFilters: Partial<ViewFilters>;
  idWorkspace: string;
}

export const DefaultViewProvider: React.FunctionComponent<DefaultViewProviderProps> = ({
  children,
  idWorkspace,
  defaultViewFilters,
}) => {
  const { mostRelevantBoards, loading } = useMostRelevantBoards({
    idOrganization: idWorkspace,
    skip: !!defaultViewFilters.boards,
  });

  const providerValue: SavedViewContextValue<SavedViewReadOnly> = useMemo(() => {
    const boardsFilter = new BoardsFilter();
    boardsFilter.setBoards(mostRelevantBoards.slice(0, maxSelectableBoards()));

    const viewFilters = {
      ...getEmptyViewFilters(),
      boards: boardsFilter,
      ...defaultViewFilters,
    };

    return {
      savedView: {
        filters: viewFilters,
        editable: false,
      },
    };
  }, [defaultViewFilters, mostRelevantBoards]);

  if (loading) {
    // TODO RMK-1489: BoardTableView should handle BoardsFilter changing
    // after initial mount. For now, wait for loading to finish before
    // mounting BoardTableView.
    return <Spinner centered />;
  }

  return (
    <SavedViewContext.Provider value={providerValue}>
      {children}
    </SavedViewContext.Provider>
  );
};

export const constructNewUrl = function (
  routeContext: RouteContext,
): ((ViewFilters: ViewFilters) => URL) | undefined {
  if (!routeContext.url) {
    return undefined;
  }
  const newUrl = new URL(routeContext.url.toString());

  return function constructUrlFilterParams(viewFilters: ViewFilters) {
    for (const [, filter] of Object.entries(viewFilters)) {
      const newUrlParams = filter.toUrlParams();

      for (const [key, value] of Object.entries(newUrlParams)) {
        if (!value) {
          newUrl.searchParams.delete(key);
        } else {
          newUrl.searchParams.set(key, value);
        }
      }
    }
    newUrl.search = newUrl.search.replace(/%2C/g, ',').replace(/%3A/g, ':');
    return newUrl;
  };
};
